import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Formik, Field, FastField } from 'formik';
import { withNamespaces } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import ModalStyles from '../../Global/Modals/Modal.styles';
import OutlinedSelectInput from '../../Global/Inputs/OutlinedSelect';
import TextFieldInput from '../../Global/Inputs/TextField';
import classnames from 'classnames';
import combineStyles from '../../../utils/combineStyles';
import LayoutStyles from '../../../styles/layout';
import SpacingStyles from '../../../styles/helpers/spacing';
import TypographyStyles from '../../../styles/typography';
import ButtonStyles from '../../../styles/button';
import Button from '@material-ui/core/Button';
import Autocomplete from '../../Global/Inputs/Autocomplete';
import Grid from '@material-ui/core/Grid';
import _get from 'lodash/get';
import _set from 'lodash/set';
import { AppContext } from '../../../AppProvider';
import EditBillingInformationSchema from './EditBillingInformation.schema';
import { COUNTRIES, STATES } from '../../../constants/dropdowns';
import _isEmpty from 'lodash/isEmpty';
import GridItem from '../../Global/GridItem';
import { searchLocations } from '../../../utils/Shipment';
import PhoneField from './../../Global/Inputs/PhoneField';
import RadioField from '../../Global/Inputs/RadioField';
import { BILL_TO } from '../../../constants/constants';
import DialogForm from '../../Global/Modals/DialogForm';
import ProgressButton from '../../Global/Buttons/Progress';

const styles = theme => ({
  contactDetails: {
    marginTop: [theme.spacing.unit * 4.25, '!important'],
    marginBottom: [theme.spacing.unit * 2, '!important']
  },
  locationDetails: {
    marginTop: 0,
    marginBottom: [theme.spacing.unit * 1, '!important']
  },
  subheadingTitle: {
    paddingTop: '0 !important',
    paddingBottom: '0 !important'
  },
  footerContent: {
    marginTop: [theme.spacing.unit * 3, '!important']
  },
  formContainer: {
    paddingTop: theme.spacing.unit * 2.5
  }
});

export class EditBillingInformation extends PureComponent {
  static propTypes = {
    onClose: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
    locations: PropTypes.array.isRequired,
    billToInfo: PropTypes.shape({
      billToAddress: PropTypes.shape({
        address1: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        postalCode: PropTypes.string,
        country: PropTypes.string
      }).isRequired,
      billToContactInfo: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
        phone: PropTypes.string
      }).isRequired
    }).isRequired
  };
  state = {
    companyName: this.props.billToInfo.companyName || '',
    firstName: '',
    formSchema: {},
    defaultCountry: '',
    isSubmitting: false,
    radioOptions: []
  };

  componentDidMount() {
    const {
      t,
      billToInfo: { billToContactInfo }
    } = this.props;
    const { defaultCountry } = this.context.globalProps.orgInfo.config;
    const formSchema = EditBillingInformationSchema({ t });
    const firstName = _get(billToContactInfo, 'firstName', '');
    const radioOptions = [
      {
        value: BILL_TO.SHIPPER,
        label: t('Shipper')
      },
      {
        value: BILL_TO.CONSIGNEE,
        label: t('Consignee')
      },
      {
        value: BILL_TO.THIRD_PARTY,
        label: t('Third Party')
      }
    ];
    this.setState({ formSchema, firstName, defaultCountry, radioOptions });
  }
  _getLocationSuggestions = async value => {
    const { locations } = this.props;
    const suggestions = searchLocations(locations, value);
    return Promise.resolve(suggestions);
  };
  _getContactSuggestions = async value => {
    const { contacts } = this.props;
    const regex = new RegExp('^' + value, 'i');
    const suggestionResults = contacts.filter(contact => {
      return regex.test(contact.title);
    });

    const suggestions = suggestionResults.map(result => {
      return {
        label: result.title,
        ...result
      };
    });

    return Promise.resolve(suggestions);
  };
  _handleChange = (field, value) => {
    this.setState({ [field]: value });
  };
  _handleLocationSelected = (location, setFieldValue) => {
    setFieldValue(
      'billToAddress.address1',
      _get(location, 'data.address.address1', '')
    );
    const address2 = location.data.address.address2
      ? location.data.address.address2
      : '';

    setFieldValue('billToAddress.address2', address2);
    setFieldValue(
      'billToAddress.city',
      _get(location, 'data.address.city', '')
    );
    setFieldValue(
      'billToAddress.state',
      _get(location, 'data.address.state', '')
    );
    setFieldValue(
      'billToAddress.country',
      _get(location, 'data.address.country', '')
    );
    setFieldValue(
      'billToAddress.postalCode',
      _get(location, 'data.address.postalCode', '')
    );
  };
  _handleContactSelected = (contact, setFieldValue) => {
    const { data: contactInfo } = contact;
    setFieldValue(
      'billToContactInfo.firstName',
      _get(contactInfo, 'firstName')
    );
    setFieldValue('billToContactInfo.lastName', _get(contactInfo, 'lastName'));
    setFieldValue('billToContactInfo.email', _get(contactInfo, 'email'));
    setFieldValue('billToContactInfo.phone', _get(contactInfo, 'phone'));
  };
  _handleValidateField = async (
    currentField,
    currentValue,
    values,
    setFieldError
  ) => {
    const { formSchema } = this.state;
    const valueCopy = { ...values };
    const updatedValues = _set(valueCopy, currentField, currentValue);

    try {
      await formSchema.validateAt(currentField, updatedValues);
      setFieldError(currentField, null);
    } catch (err) {
      setFieldError(currentField, err.message);
    }
  };
  _handleOnSave = (validateForm, values) => {
    const { onSave } = this.props;

    validateForm().then(response => {
      if (_isEmpty(response)) {
        this.setState({ isSubmitting: true });
        onSave(values);
        this.setState({ isSubmitting: false });
      }
    });
  };

  render() {
    const {
      open,
      onClose,
      t,
      classes,
      billToInfo: { billTo, billToAddress, billToContactInfo }
    } = this.props;
    const { companyName, defaultCountry } = this.state;
    const initialValues = {
      billTo,
      billToAddress,
      billToContactInfo,
      companyName
    };
    initialValues.billToAddress.country = billToAddress.country
      ? billToAddress.country
      : defaultCountry;
    //if no country, then select the default
    const { firstName, isSubmitting, radioOptions } = this.state;
    return (
      <Formik
        validationSchema={this.state.formSchema}
        initialValues={initialValues}
        validateOnChange={false}
        validateOnBlur={false}
        enableReinitialize={false}
        render={({
          values,
          handleChange,
          setFieldError,
          setFieldValue,
          validateForm,
          resetForm
        }) => (
          <DialogForm
            open={open}
            onClose={onClose}
            title={`${t('Bill to')}:`}
            footer={
              <Fragment>
                <div className={classes.spacer} />
                <Button
                  color={'primary'}
                  className={classes.textButton}
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                >
                  {t('Cancel')}
                </Button>
                <ProgressButton
                  color="primary"
                  className={classnames(classes.button)}
                  classes={{
                    disabled: classes.buttonDisabled,
                    label: classnames(classes.buttonLabel)
                  }}
                  onClick={() => {
                    this._handleOnSave(validateForm, values);
                  }}
                  isSubmitting={isSubmitting}
                >
                  {t('Save')}
                </ProgressButton>
              </Fragment>
            }
          >
            <Field
              name={`billTo`}
              render={({ field, form }) => {
                return (
                  <RadioField
                    options={radioOptions}
                    onChange={handleChange}
                    field={field}
                    form={form}
                    value={values.billTo}
                  />
                );
              }}
            />
            {values.billTo === BILL_TO.THIRD_PARTY && (
              <Grid
                container
                spacing={16}
                classes={{
                  container: classes.formContainer
                }}
              >
                <Grid item lg={12} classes={{ item: classes.subheadingTitle }}>
                  <p
                    className={classnames(
                      classes.locationDetails,
                      classes.headline7
                    )}
                  >
                    {t('Company Details')}
                  </p>
                </Grid>
                <GridItem lg={12}>
                  <Field
                    name={`companyName`}
                    render={({ field, form }) => {
                      return (
                        <Autocomplete
                          value={companyName}
                          getSuggestions={this._getLocationSuggestions}
                          classes={{ root: classes.locations }}
                          getSuggestionValue={suggestion => {
                            return suggestion.label;
                          }}
                          onSuggestionSelected={location => {
                            this._handleLocationSelected(
                              location,
                              setFieldValue
                            );
                          }}
                          label={t('Company Name')}
                          onChange={change => {
                            this._handleChange('companyName', change);

                            setFieldValue('companyName', change);
                          }}
                          field={field}
                          form={form}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem lg={6}>
                  <FastField
                    value={_get(values, 'billToAddress.address1', '')}
                    name={`billToAddress.address1`}
                    label={t('Address')}
                    onChange={handleChange}
                    component={TextFieldInput}
                    validateField={(currentField, currentValue) => {
                      this._handleValidateField(
                        currentField,
                        currentValue,
                        values,
                        setFieldError
                      );
                    }}
                  />
                </GridItem>
                <GridItem lg={6}>
                  <FastField
                    value={_get(values, 'billToAddress.address2', '')}
                    name={`billToAddress.address2`}
                    label={t('Address 2')}
                    onChange={handleChange}
                    component={TextFieldInput}
                  />
                </GridItem>
                <GridItem lg={6} md={6}>
                  <Field
                    name={`billToAddress.country`}
                    onChange={handleChange}
                    render={({ field, form }) => {
                      return (
                        <OutlinedSelectInput
                          value={_get(values, 'billToAddress.country', '')}
                          field={field}
                          form={form}
                          label={t('Country')}
                          options={COUNTRIES}
                          multiple={false}
                          classes={{}}
                          validateField={(currentField, currentValue) => {
                            this._handleValidateField(
                              currentField,
                              currentValue,
                              values,
                              setFieldError
                            );
                          }}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem lg={6} md={6}>
                  <FastField
                    value={_get(values, 'billToAddress.city', '')}
                    name={'billToAddress.city'}
                    label={t('City')}
                    onChange={handleChange}
                    component={TextFieldInput}
                    validateField={(currentField, currentValue) => {
                      this._handleValidateField(
                        currentField,
                        currentValue,
                        values,
                        setFieldError
                      );
                    }}
                  />
                </GridItem>
                <GridItem lg={6} md={6}>
                  <Field
                    name={`billToAddress.state`}
                    onChange={handleChange}
                    render={({ field, form }) => {
                      return (
                        <OutlinedSelectInput
                          value={_get(values, 'billToAddress.state', '')}
                          field={field}
                          form={form}
                          label={t('State/Province')}
                          options={STATES[values.billToAddress.country]}
                          multiple={false}
                          classes={{
                            root: classes.dropdownsMargin
                          }}
                          validateField={(currentField, currentValue) => {
                            this._handleValidateField(
                              currentField,
                              currentValue,
                              values,
                              setFieldError
                            );
                          }}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem lg={6} md={6}>
                  <FastField
                    value={_get(values, 'billToAddress.postalCode', '')}
                    name={`billToAddress.postalCode`}
                    label={t('Zip Code')}
                    onChange={handleChange}
                    component={TextFieldInput}
                    validateField={(currentField, currentValue) => {
                      this._handleValidateField(
                        currentField,
                        currentValue,
                        values,
                        setFieldError
                      );
                    }}
                  />
                </GridItem>
                <Grid item lg={12} classes={{ item: classes.subheadingTitle }}>
                  <p className={classnames(classes.headline7)}>
                    {t('Contact Details')}
                  </p>
                </Grid>
                <GridItem lg={6}>
                  <Field
                    name={`billToContactInfo.firstName`}
                    render={({ field, form }) => {
                      return (
                        <Autocomplete
                          value={firstName || ''}
                          validateField={(currentField, currentValue) => {
                            this._handleValidateField(
                              currentField,
                              currentValue,
                              values,
                              setFieldError
                            );
                          }}
                          getSuggestions={this._getContactSuggestions}
                          getSuggestionValue={suggestion => {
                            return _get(suggestion, 'data.firstName', '');
                          }}
                          onSuggestionSelected={contact => {
                            this._handleContactSelected(contact, setFieldValue);
                          }}
                          label={t('First Name')}
                          onChange={change => {
                            this._handleChange('firstName', change);
                          }}
                          field={field}
                          form={form}
                        />
                      );
                    }}
                  />
                </GridItem>
                <GridItem lg={6}>
                  <FastField
                    value={_get(values, 'billToContactInfo.lastName', '')}
                    name={`billToContactInfo.lastName`}
                    label={t('Last Name')}
                    onChange={handleChange}
                    component={TextFieldInput}
                    validateField={(currentField, currentValue) => {
                      this._handleValidateField(
                        currentField,
                        currentValue,
                        values,
                        setFieldError
                      );
                    }}
                  />
                </GridItem>
                <GridItem lg={6}>
                  <Field
                    value={_get(values, 'billToContactInfo.phone', '')}
                    name={`billToContactInfo.phone`}
                    label={t('Phone')}
                    onChange={handleChange}
                    component={PhoneField}
                    country={_get(values, 'billToAddress.country', '')}
                    validateField={(currentField, currentValue) => {
                      this._handleValidateField(
                        currentField,
                        currentValue,
                        values,
                        setFieldError
                      );
                    }}
                  />
                </GridItem>
                <GridItem lg={6}>
                  <FastField
                    value={_get(values, 'billToContactInfo.email', '')}
                    name={`billToContactInfo.email`}
                    label={t('Email')}
                    onChange={handleChange}
                    component={TextFieldInput}
                    validateField={(currentField, currentValue) => {
                      this._handleValidateField(
                        currentField,
                        currentValue,
                        values,
                        setFieldError
                      );
                    }}
                  />
                </GridItem>
              </Grid>
            )}
          </DialogForm>
        )}
      />
    );
  }
}

EditBillingInformation.contextType = AppContext;
const translatedEditBillingInformation = withNamespaces()(
  EditBillingInformation
);
export default withStyles(
  combineStyles(
    styles,
    TypographyStyles,
    ModalStyles,
    LayoutStyles,
    SpacingStyles,
    ButtonStyles
  )
)(translatedEditBillingInformation);

//export default translatedEditBillingInformation;
